import csvDownload from 'json-to-csv-export';
import { useTranslation } from 'react-i18next';
import { getGermanTaxCsvByUser, getGermanTaxCsvByUserByAsset } from 'src/api/stoApi';
import { IGermanTax, IGermanTaxByUserByAsset, ITaxReportGermany, SPV } from 'src/models/index';
import { awaitUtil, sortCSVHeader } from 'src/util/utils';
import DownloadIcon from '@mui/icons-material/Download';
import { Chip } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

function createData(month: number, year: number, gross: string, taxes: string) {
  // We add +1 to the month because the month is 0-indexed
  const monthUser = month + 1;
  const monthString = monthUser.toString().padStart(2, '0');
  const date = `${monthString}.${year}`;
  return { date: date, gross, taxes, month: month, year: year };
}

type TTableRow = {
  date: string;
  gross: string;
  taxes: string;
  month: number;
  year: number;
};

export const TaxReportTableGermany = ({ spv, taxReport }: { spv: SPV; taxReport: ITaxReportGermany[] }) => {
  const { t } = useTranslation();
  // Pagination deactivated for now. Will activate once backend supports pagination
  // const [page, setPage] = React.useState(0);
  // const [rowsPerPage, setRowsPerPage] = React.useState(10);
  //   const [rows, setRows] = useState<TableRow[]>([]);
  const transactionData = taxReport;

  const rows = transactionData.map((row) => createData(row.month, row.year, row.gross, row.taxes));

  const desiredKeyOrder: Array<keyof IGermanTax> = [
    'userId',
    'firstName',
    'lastName',
    'capitalIncomeGross',
    'capitalIncomeNet',
    'sumOfTax',
    'KapESt',
    'Soli',
    'Kirchensteuer',
    'birthDate',
    'address',
    'zipCode',
    'taxId',
    'city',
    'country',
    'churchTaxLevel',
  ];

  const downloadReportByUser = async (row: TTableRow) => {
    const [data, error] = await awaitUtil(getGermanTaxCsvByUser(spv.id, row.year.toString(), row.month.toString()));
    if (error) return;
    if (!data) return;
    const reportByUser = data.data.data;
    const sortedItems = sortCSVHeader(reportByUser, desiredKeyOrder);

    const dataToConvert = {
      data: sortedItems,
      filename: 'issuer_tax_report',
      delimiter: ';',
      headers: [
        t('issuer.germanTax.table.userId'),
        t('issuer.germanTax.table.firstName'),
        t('issuer.germanTax.table.lastName'),
        t('issuer.germanTax.table.capitalIncomeGross'),
        t('issuer.germanTax.table.capitalIncomeNet'),
        t('issuer.germanTax.table.sumOfTax'),
        t('issuer.germanTax.table.KapESt'),
        t('issuer.germanTax.table.Soli'),
        t('issuer.germanTax.table.Kirchensteuer'),
        t('issuer.germanTax.table.birthDate'),
        t('issuer.germanTax.table.address'),
        t('issuer.germanTax.table.zipCode'),
        t('issuer.germanTax.table.taxIds'),
        t('issuer.germanTax.table.city'),
        t('issuer.germanTax.table.country'),
        t('issuer.germanTax.table.churchTaxLevel'),
      ],
    };
    csvDownload(dataToConvert);
  };

  const downloadReportByUserByAsset = async (row: TTableRow) => {
    const [data, error] = await awaitUtil(getGermanTaxCsvByUserByAsset(spv.id, row.year.toString(), row.month.toString()));
    if (error) return;
    if (!data) return;

    const reportByUserByAsset = data.data.data;
    const csvData = dataTransformer(reportByUserByAsset);
    // Transform to array instead of object
    const dataToConvert = {
      data: csvData,
      filename: 'issuer_tax_report',
      delimiter: ';',
      headers: [
        t('issuer.germanTax.table.capitalIncomeGross'),
        t('issuer.germanTax.table.capitalIncomeNet'),
        t('issuer.germanTax.table.sumOfTax'),
        t('issuer.germanTax.table.KapESt'),
        t('issuer.germanTax.table.Soli'),
        t('issuer.germanTax.table.Kirchensteuer'),
        t('issuer.germanTax.table.userId'),
        t('issuer.germanTax.table.firstName'),
        t('issuer.germanTax.table.lastName'),
        t('issuer.germanTax.table.birthDate'),
        t('issuer.germanTax.table.address'),
        t('issuer.germanTax.table.zipCode'),
        t('issuer.germanTax.table.taxIds'),
        t('issuer.germanTax.table.city'),
        t('issuer.germanTax.table.country'),
        t('issuer.germanTax.table.assetId'),
        t('issuer.germanTax.table.churchTaxLevel'),
      ],
    };
    csvDownload(dataToConvert);
  };

  interface GermanTaxByUserWithAsset extends IGermanTax {
    assetId: string;
  }

  function dataTransformer(report: IGermanTaxByUserByAsset): GermanTaxByUserWithAsset[] {
    const transformedData: GermanTaxByUserWithAsset[] = [];
    for (const asset in report) {
      for (const user in report[asset]) {
        const transformedRow = {
          ...report[asset][user],
          assetId: asset,
        };
        delete transformedRow.taxIds;
        transformedData.push(transformedRow);
      }
    }
    return transformedData;
  }

  // Pagination deactivated for now. Will activate once backend supports pagination
  // const handleChangePage = (event: unknown, newPage: number) => {
  //   setPage(newPage);
  // };
  // Pagination deactivated for now. Will activate once backend supports pagination
  // const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setRowsPerPage(Number(event.target.value));
  //   setPage(0);
  // };

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t('issuer.tax.table.monthYear')}</TableCell>
              <TableCell>{t('issuer.tax.table.grossCapIncome')}</TableCell>
              <TableCell>{t('issuer.tax.table.tax')}</TableCell>
              <TableCell align="center">{t('issuer.tax.table.report')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: TTableRow) => (
              <TableRow key={row.date} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component="th" scope="row">
                  {row.date.padStart(2, '0')}
                </TableCell>
                <TableCell align="left">{row.gross} €</TableCell>
                <TableCell align="left">{row.taxes} €</TableCell>
                <TableCell align="right">
                  <Chip sx={{ mr: 2 }} icon={<DownloadIcon />} onClick={() => downloadReportByUser(row)} label="By user" />
                  <Chip icon={<DownloadIcon />} onClick={() => downloadReportByUserByAsset(row)} label="By user by asset" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* 
      Pagination deactivated for now. Will activate once backend supports pagination
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={-1}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        nextIconButtonProps={
          rows.length < rowsPerPage
            ? {
                disabled: true,
              }
            : undefined
        }
      /> */}
    </>
  );
};
